import { formatMoney, getGiftFunnel } from '@bbd/gwp-funnel';
import {
  GIFT_REMOVED_KEY,
  isNoticeVisible,
  noticeTimeLeft,
  VOORDEELSET_NOTICE_EVENT as NOTICE_EVENT,
  VOORDEELSET_NOTICE_KEY as NOTICE_KEY
} from '@bbd/cart-rules';

const readNoticeExpiry = () => {
  try {
    return parseInt(sessionStorage.getItem(NOTICE_KEY) ?? '0', 10) || 0;
  } catch {
    return 0;
  }
};

const writeNoticeExpiry = (expiry) => {
  try {
    if (expiry) sessionStorage.setItem(NOTICE_KEY, String(expiry));
    else sessionStorage.removeItem(NOTICE_KEY);
  } catch {
    // sessionStorage unavailable, the notice just won't persist
  }
};

const readRemovedFlag = () => {
  try {
    return sessionStorage.getItem(GIFT_REMOVED_KEY) === '1';
  } catch {
    return false;
  }
};

const clearRemovedFlag = () => {
  try {
    sessionStorage.removeItem(GIFT_REMOVED_KEY);
  } catch {
    // sessionStorage unavailable, the removed state just won't persist
  }
};

class BbdGwpProgressBarComponent extends HTMLElement {
  #observer = null;

  /** Threshold in cents from the Monk funnel, the server renders a fixed one until it is loaded */
  #funnelThreshold = null;

  #noticeTimer = null;

  /** Remaining time (ms) while the pointer or focus is on the notice, null when the timer runs */
  #noticePausedRemaining = null;

  connectedCallback() {
    document.addEventListener(NOTICE_EVENT, this.#onNoticeEvent);
    this.addEventListener('click', this.#onClick);
    this.#applyExchangeRate();
    this.#loadFunnelThreshold();

    // A background cart refresh morphs the server markup back in: re-apply when the bar is replaced or its
    // state attributes are reset (data-state is only set by this script, so morph removing it means the text is reset too)
    this.#observer = new MutationObserver(() => this.#applyExchangeRate());
    this.#observer.observe(this, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['data-amount', 'data-gift-in-cart', 'data-state', 'data-voordeelset']
    });
  }

  disconnectedCallback() {
    document.removeEventListener(NOTICE_EVENT, this.#onNoticeEvent);
    this.removeEventListener('click', this.#onClick);
    window.clearTimeout(this.#noticeTimer);
    this.#observer?.disconnect();
    this.#observer = null;
  }

  /**
   * The threshold of the Monk funnel is the one the gift picker locks and unlocks the gift with, so the bar uses it too.
   * Without a funnel (call failed, no funnel for the currency) the bar keeps the threshold of the server.
   */
  async #loadFunnelThreshold() {
    const shopUrl = this.querySelector('.bbd-gwp-progress-bar')?.dataset.shopUrl;
    if (!shopUrl) return;

    try {
      const { threshold } = await getGiftFunnel(shopUrl);
      this.#funnelThreshold = threshold;
      this.#applyExchangeRate();
    } catch {
      // The gift picker logs the failure and falls back to the Monk widget
    }
  }

  #onNoticeEvent = () => this.#applyNotice();

  #onClick = (event) => {
    if (!(event.target instanceof Element) || !event.target.closest('.bbd-gwp-progress-bar__notice-close')) return;

    this.#noticePausedRemaining = null;
    writeNoticeExpiry(0);

    // The focused close button is about to be removed: keep focus in the cart, on the bar with its message
    const bar = this.querySelector('.bbd-gwp-progress-bar');
    if (bar instanceof HTMLElement) {
      bar.setAttribute('tabindex', '-1');
      bar.focus({ preventScroll: true });
    }

    this.#applyNotice();
  };

  /**
   * The notice under the bar: shown from the moment the guard removed the gift until the stored expiry, with a close
   * button, paused while the pointer or focus is on it. It lives in the persistent live region of the bar, so screen
   * readers announce it, and it is re-added after every morph of the cart section.
   */
  #applyNotice() {
    const bar = this.querySelector('.bbd-gwp-progress-bar');
    const live = this.querySelector('.bbd-gwp-progress-bar__notice');
    if (!(bar instanceof HTMLElement) || !(live instanceof HTMLElement)) return;

    const paused = this.#noticePausedRemaining !== null;
    const expiry = readNoticeExpiry();
    const text = bar.dataset.noticeVoordeelset ?? '';
    const visible = Boolean(text) && isNoticeVisible(expiry, Date.now(), paused);
    const box = live.querySelector('.bbd-gwp-progress-bar__notice-box');

    if (!visible) {
      window.clearTimeout(this.#noticeTimer);
      this.#noticePausedRemaining = null;
      if (expiry) writeNoticeExpiry(0);
      box?.remove();
      return;
    }

    if (!box) {
      const template = this.querySelector('template[data-notice-template]');
      const fresh =
        template instanceof HTMLTemplateElement ? template.content.firstElementChild?.cloneNode(true) : null;
      if (!(fresh instanceof HTMLElement)) return;

      // A morph re-creates the box: the pointer is not on the new one, so no mouseleave would ever resume the timer
      this.#noticePausedRemaining = null;
      fresh.querySelector('.bbd-gwp-progress-bar__notice-text').textContent = text;
      for (const type of ['mouseenter', 'focusin']) fresh.addEventListener(type, () => this.#pauseNotice());
      for (const type of ['mouseleave', 'focusout']) fresh.addEventListener(type, () => this.#resumeNotice(fresh));
      live.append(fresh);
    }

    if (!paused) {
      window.clearTimeout(this.#noticeTimer);
      this.#noticeTimer = window.setTimeout(() => this.#applyNotice(), noticeTimeLeft(expiry, Date.now()));
    }
  }

  #pauseNotice() {
    if (this.#noticePausedRemaining !== null) return;

    window.clearTimeout(this.#noticeTimer);
    this.#noticePausedRemaining = noticeTimeLeft(readNoticeExpiry(), Date.now());
  }

  /** @param {HTMLElement} box */
  #resumeNotice(box) {
    if (this.#noticePausedRemaining === null) return;
    // Pointer still over it, or focus moved to another part of it: stay paused
    if (box.matches(':hover') || box.matches(':focus-within')) return;

    writeNoticeExpiry(Date.now() + this.#noticePausedRemaining);
    this.#noticePausedRemaining = null;
    this.#applyNotice();
  }

  /** @param {HTMLElement} bar */
  #applyVoordeelset(bar) {
    const fill = bar.querySelector('.bbd-gwp-progress-bar__fill');
    const message = bar.querySelector('.bbd-gwp-progress-bar__message');
    if (fill instanceof HTMLElement) fill.style.width = '100%';
    bar.dataset.completed = 'true';
    if (bar.dataset.state !== 'voordeelset') bar.dataset.state = 'voordeelset';
    // Only write when it changed: the observer in connectedCallback would otherwise retrigger itself
    const text = bar.dataset.messageVoordeelset ?? '';
    if (message instanceof HTMLElement && message.textContent !== text) message.textContent = text;
  }

  #applyExchangeRate() {
    const bar = this.querySelector('.bbd-gwp-progress-bar');
    if (!(bar instanceof HTMLElement)) return;

    this.#applyNotice();

    // A voordeelset already contains a gift: its own message and a full bar, whatever the amount
    if (bar.dataset.voordeelset === 'true') {
      this.#applyVoordeelset(bar);
      return;
    }

    const baseThreshold = parseInt(bar.dataset.baseThreshold ?? '17500', 10);
    const amount = parseInt(bar.dataset.amount ?? '0', 10);
    const needsJs = bar.dataset.thresholdNeedsJs === 'true';
    const rate = parseFloat(window.Shopify?.currency?.rate ?? '1');
    const effectiveRate = Number.isFinite(rate) && rate > 0 ? rate : 1;
    const threshold = this.#funnelThreshold ?? (needsJs ? Math.round(baseThreshold * effectiveRate) : baseThreshold);

    const remaining = Math.max(0, threshold - amount);
    const progress = Math.min(100, (amount / threshold) * 100);
    const isCompleted = amount >= threshold;

    const fill = bar.querySelector('.bbd-gwp-progress-bar__fill');
    const message = bar.querySelector('.bbd-gwp-progress-bar__message');

    if (fill instanceof HTMLElement) {
      fill.style.width = `${progress}%`;
    }

    bar.dataset.completed = String(isCompleted);

    const giftInCart = bar.dataset.giftInCart === 'true';
    if (isCompleted) clearRemovedFlag();
    const isRemoved = !isCompleted && !giftInCart && readRemovedFlag();

    let state = 'below';
    if (isCompleted) state = giftInCart ? 'chosen' : 'unlocked';
    else if (isRemoved) state = 'removed';
    // Only write when it changed: assigning the same value still queues a mutation for the observer
    if (bar.dataset.state !== state) bar.dataset.state = state;

    if (message instanceof HTMLElement) {
      const templates = {
        below: bar.dataset.messageRemaining,
        removed: bar.dataset.messageRemoved,
        unlocked: bar.dataset.messageUnlocked,
        chosen: bar.dataset.messageChosen
      };
      const template = templates[state] ?? '';
      const text = template.replace('[amount]', this.#formatMoney(remaining));
      // Only write when it changed: the observer in connectedCallback would otherwise retrigger itself
      if (message.textContent !== text) message.textContent = text;
    }
  }

  #formatMoney(cents) {
    const templateEl = this.querySelector('template[data-money-format]');
    const format = templateEl?.content.textContent?.trim() ?? '{{amount_with_comma_separator}}';

    return formatMoney(cents, format);
  }
}

if (!customElements.get('bbd-gwp-progress-bar-component')) {
  customElements.define('bbd-gwp-progress-bar-component', BbdGwpProgressBarComponent);
}
